import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Phone, MessageSquare, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { PanelHeader } from './PanelHeader';
import { PersonalAgentChat } from '@/components/ai/PersonalAgentChat';
import { EmbeddedSoftphoneContent } from '@/components/softphone/SoftphoneWidget';
import { InternalChatPanel } from './InternalChatPanel';
import type { PanelState, PanelKey, CommunicationPanelStates } from '@/types/communication-panels';

interface CommunicationPanelContainerProps {
  states: CommunicationPanelStates;
  onStateChange: (panel: PanelKey, state: PanelState) => void;
  onPopOut: (panel: PanelKey) => void;
  phoneStatus: string;
  callSummaryPrompt: string | null;
  pendingCallSid: string | null;
  handleSaveNote?: (noteText: string) => void;
}

const PANEL_HEIGHT = 520;
const MINIMIZED_HEIGHT = 40;

export function CommunicationPanelContainer({
  states,
  onStateChange,
  onPopOut,
  phoneStatus,
  callSummaryPrompt,
  pendingCallSid,
  handleSaveNote,
}: CommunicationPanelContainerProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [mobileActivePanel, setMobileActivePanel] = useState<PanelKey>('omstilling');

  const handleCreateCaseFromCall = useCallback(() => {
    if (!callSummaryPrompt) return;
    sessionStorage.setItem('wizard_prefill_transcript', callSummaryPrompt);
    sessionStorage.setItem('wizard_prefill_source', 'call_summary');
    navigate('/office/cases/new');
  }, [callSummaryPrompt, navigate]);

  const phoneRinging = phoneStatus === 'ringing';

  const openPanels: PanelKey[] = (['omstilling', 'ai_assistent', 'intern_chat'] as const).filter(
    k => states[k] !== 'closed'
  );

  if (openPanels.length === 0) return null;

  const panelConfig: Record<PanelKey, { title: string; icon: React.ReactNode; iconColor: string }> = {
    omstilling: { title: 'Omstilling', icon: <Phone className="w-4 h-4" />, iconColor: 'text-emerald-500' },
    ai_assistent: { title: 'AI Assistent', icon: <Bot className="w-4 h-4" />, iconColor: 'text-primary' },
    intern_chat: { title: 'Intern Chat', icon: <MessageSquare className="w-4 h-4" />, iconColor: 'text-blue-500' },
  };

  function renderPanelContent(key: PanelKey) {
    switch (key) {
      case 'omstilling':
        return <EmbeddedSoftphoneContent onBack={() => onStateChange('omstilling', 'closed')} />;
      case 'ai_assistent':
        return (
          <>
            <PersonalAgentChat
              mode="office"
              embedded
              compact
              initialMessage={callSummaryPrompt || undefined}
              onSaveNote={pendingCallSid ? handleSaveNote : undefined}
              showSaveNote={!!pendingCallSid}
            />
            {callSummaryPrompt && (
              <div className="px-3 pb-3">
                <button
                  onClick={handleCreateCaseFromCall}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Opret sag fra opkald
                </button>
              </div>
            )}
          </>
        );
      case 'intern_chat':
        return <InternalChatPanel embedded />;
    }
  }

  // Mobile: one panel at a time with tab switcher
  if (isMobile) {
    const showTabs = openPanels.length > 1;
    const activePanel = showTabs && openPanels.includes(mobileActivePanel)
      ? mobileActivePanel
      : openPanels[0];
    const activeState = states[activePanel];

    return (
      <div
        className={cn(
          "fixed bottom-4 right-4 left-4 z-50 bg-background border border-border rounded-xl shadow-2xl overflow-hidden",
          "transition-all duration-200 ease-out"
        )}
        style={{
          height: activeState === 'minimized' ? MINIMIZED_HEIGHT : PANEL_HEIGHT,
          maxHeight: 'calc(100vh - 2rem)',
        }}
      >
        {showTabs && activeState !== 'minimized' && (
          <div className="flex border-b border-border">
            {openPanels.map(key => {
              const cfg = panelConfig[key];
              return (
                <button
                  key={key}
                  onClick={() => setMobileActivePanel(key)}
                  className={cn("flex-1 py-2 text-xs font-medium transition-colors",
                    mobileActivePanel === key
                      ? "bg-primary/10 text-primary border-b-2 border-primary"
                      : "text-muted-foreground"
                  )}
                >
                  <span className="inline mr-1">{cfg.icon}</span>{cfg.title}
                </button>
              );
            })}
          </div>
        )}

        <PanelHeader
          title={panelConfig[activePanel].title}
          icon={panelConfig[activePanel].icon}
          state={activeState}
          onMinimize={() => onStateChange(activePanel, 'minimized')}
          onClose={() => onStateChange(activePanel, 'closed')}
          onRestore={() => onStateChange(activePanel, 'open')}
          onPopOut={() => onPopOut(activePanel)}
          showNotificationBadge={activePanel === 'omstilling' && phoneRinging && activeState === 'minimized'}
        />

        {activeState === 'open' && (
          <div className="flex-1 overflow-hidden" style={{ height: `calc(100% - ${showTabs ? '77px' : '40px'})` }}>
            {renderPanelContent(activePanel)}
          </div>
        )}
      </div>
    );
  }

  // Desktop: side-by-side panels
  const panelWidth = (key: PanelKey) => states[key] === 'minimized' ? 200 : 340;
  const gap = 8;

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-50 flex gap-2 items-end",
        "transition-all duration-200 ease-out"
      )}
      style={{ maxWidth: 'calc(100vw - 2rem)' }}
    >
      {openPanels.map(key => {
        const cfg = panelConfig[key];
        const state = states[key];
        return (
          <div
            key={key}
            className={cn(
              "bg-background border border-border rounded-xl shadow-2xl overflow-hidden flex flex-col",
              "transition-all duration-200 ease-out",
            )}
            style={{
              width: panelWidth(key),
              height: state === 'minimized' ? MINIMIZED_HEIGHT : PANEL_HEIGHT,
              maxHeight: 'calc(100vh - 2rem)',
            }}
          >
            <PanelHeader
              title={cfg.title}
              icon={cfg.icon}
              state={state}
              onMinimize={() => onStateChange(key, 'minimized')}
              onClose={() => onStateChange(key, 'closed')}
              onRestore={() => onStateChange(key, 'open')}
              onPopOut={() => onPopOut(key)}
              showNotificationBadge={key === 'omstilling' && phoneRinging && state === 'minimized'}
            />
            {state === 'open' && (
              <div className="flex-1 overflow-hidden">
                {renderPanelContent(key)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
